import React from 'react'
import styled from 'styled-components'
import { useMediaQuery } from 'react-responsive'
import { useTranslation } from 'react-i18next'
import {
  LayoutFooter as LayoutFooterUI,
  LayoutFooterLogo,
  LayoutFooterSeparator as FooterSeparator,
  LayoutFooterMenuItem as MenuItem,
  LayoutFooterLanguageSelect,
  LayoutFooterTitle as Title,
  mediaQuery,
  Small,
  breakpoints,
  Col as ColUI,
  Row as RowUI,
  LayoutFooterDropdownCollapse,
  DropdownToggle
} from '@ppay/client'

type Links = {
  title: string | React.ReactNode
  link?: string
  id: string
}

type MenuItems = {
  title: string
  links: Links[]
}

const LayoutFooter = styled(LayoutFooterUI)`
  background-color: ${({ theme }) => theme.colors.tertiary};
`

const Logo = styled(LayoutFooterLogo)`
  max-width: 225px;
  ${mediaQuery.lessThan('md')`
    margin-bottom: 13px;
  `}
  ${mediaQuery.greaterThan('xl')`
    margin-bottom: 13px;
  `}
`

const Col = styled(ColUI)`
  &:first-child {
    ${mediaQuery.lessThan('xxl')`
      margin-bottom: 30px;
  `}
  }
  ${mediaQuery.lessThan('md')`
    margin-bottom: 20px;
    &:first-child {
      margin-bottom: 30px;
    }
    &:last-child {
      margin-bottom: 0;
      margin-top: 10px;
    }
  `}
`

const Row = styled(RowUI)`
  width: -webkit-fill-available;
  width: -moz-available;
`

const LanguageSelect = styled(LayoutFooterLanguageSelect)`
  ${mediaQuery.greaterThan('lg')`
    margin-bottom: 30px;
`}
  ${mediaQuery.lessThan('md')`
    margin-top: 10px;
`}
`

export const Footer = ({ logo, onLogoClick }: { logo: string; onLogoClick: () => unknown }) => {
  const {
    t,
    i18n: { language }
  } = useTranslation('layout')
  const isSm = useMediaQuery({ maxWidth: breakpoints.sm })

  const menuItems: MenuItems[] = [
    {
      title: t('footer.links.products.title'),
      links: [
        {
          title: t('footer.links.products.links.walletSdk'),
          link: process.env.REACT_APP_WALLET_SDK_URL,
          id: 'myfooter-wallet'
        }
      ]
    },
    {
      title: t('footer.links.info.title'),
      links: [
        {
          title: t('footer.links.info.links.about'),
          link: `${process.env.REACT_APP_SITE_URL}/${language}/about`,
          id: 'myfooter-about'
        },
        {
          title: t('footer.links.info.links.support'),
          link: process.env.REACT_APP_CHAT,
          id: 'myfooter-support'
        },
        {
          title: t('footer.links.info.links.termsOfService'),
          link: process.env.REACT_APP_TERMS_URL,
          id: 'footer-terms'
        },
        {
          title: t('footer.links.info.links.privacyPolicy'),
          link: process.env.REACT_APP_PRIVACY_URL,
          id: 'footer-privacy'
        }
      ]
    }
  ]

  return (
    <LayoutFooter>
      <Row className="justify-content-between">
        <Col
          className="d-flex justify-content-center justify-content-md-start order-0 flex-column align-items-center align-items-md-start"
          xs={12}
          md={6}
          xl="auto"
        >
          <Logo src={logo} onClick={onLogoClick} />
        </Col>
        {menuItems.map((col, i) => (
          <Col
            key={i}
            xs={12}
            md="auto"
            className={`d-flex flex-column align-items-center align-items-xl-start order-${
              i + 1
            } order-md-${i + 2} order-xl-${i + 1}`}
          >
            {isSm ? (
              <MobileMenuDropdown id={'menuLinks' + i} title={col.title} links={col.links} />
            ) : (
              <>
                <Title className="mb-3">{col.title}</Title>
                {col.links.map((l, i) => (
                  <MenuItem id={l.id} key={i} link={l.link} target="_blank">
                    {l.title}
                  </MenuItem>
                ))}
              </>
            )}
          </Col>
        ))}
        <Col
          className={`d-flex justify-content-center justify-content-md-end order-${
            menuItems.length + 1
          } order-md-1 order-xl-${menuItems.length + 1}`}
          xs={12}
          md={6}
          xl="auto"
        >
          <div>
            <LanguageSelect />
          </div>
        </Col>
      </Row>
      <FooterSeparator />
      <Small className="text-center">{t('footer.text')}</Small>
    </LayoutFooter>
  )
}

const MobileMenuDropdown = ({
  id,
  title,
  links
}: {
  id: string
  title: string
  links: Links[]
}) => (
  <>
    <DropdownToggle id={id} onClick={function noRefCheck() {}}>
      <Title className="mb-0">{title}</Title>
    </DropdownToggle>
    <LayoutFooterDropdownCollapse toggler={id}>
      {links.map((l, i) => (
        <MenuItem id={l.id} key={i} link={l.link} target="_blank">
          {l.title}
        </MenuItem>
      ))}
    </LayoutFooterDropdownCollapse>
  </>
)
